import { useCallback, useRef } from 'react';
import { useAIChatStore } from '@/store/useAIChatStore';
import { aiChatApi } from '@/api/ai-chat.api';
import { ChatStatus, IChatMessage } from '@/types/ai-chat.types';
import { useActiveCompanyId } from '@/queries/dashboard.queries';

export function useAIChat() {
  const {
    messages,
    status,
    isStreaming,
    streamingMessageId,
    sessionId,
    addMessage,
    updateMessage,
    clearMessages: clearLocalMessages,
    setMessages,
    setStatus,
    setIsStreaming,
    setStreamingMessageId,
    setSessionId,
  } = useAIChatStore();

  const companyId = useActiveCompanyId();
  const animationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isStreaming || !companyId) return;

      // Stop any running animation
      if (animationIntervalRef.current) clearInterval(animationIntervalRef.current);

      addMessage({
        id: crypto.randomUUID(),
        role: 'user',
        content: text.trim(),
        timestamp: new Date().toISOString(),
      });

      const assistantMessageId = crypto.randomUUID();
      addMessage({ id: assistantMessageId, role: 'assistant', content: '', timestamp: new Date().toISOString() });

      setStatus(ChatStatus.SENDING);

      try {
        const result = await aiChatApi.sendMessage(companyId, text.trim(), sessionId);

        setSessionId(result.sessionId);

        // Animate the response word-by-word, stripping chart references from animated text
        const textOnly = result.response.replace(/\{\{chart:[^}]+\}\}/g, '').trim();
        const words = textOnly.split(' ');
        let wordIndex = 0;

        setStatus(ChatStatus.STREAMING);
        setIsStreaming(true);
        setStreamingMessageId(assistantMessageId);

        animationIntervalRef.current = setInterval(() => {
          wordIndex++;
          updateMessage(assistantMessageId, { content: words.slice(0, wordIndex).join(' ') });

          if (wordIndex >= words.length) {
            clearInterval(animationIntervalRef.current!);
            animationIntervalRef.current = null;
            updateMessage(assistantMessageId, {
              content: result.response,
              chart_data: result.charts?.length > 0 ? result.charts : undefined,
            });
            setIsStreaming(false);
            setStreamingMessageId(null);
            setStatus(ChatStatus.IDLE);
          }
        }, 30);
      } catch {
        updateMessage(assistantMessageId, { content: 'Something went wrong. Please try again.' });
        setIsStreaming(false);
        setStreamingMessageId(null);
        setStatus(ChatStatus.IDLE);
      }
    },
    [
      isStreaming,
      companyId,
      sessionId,
      addMessage,
      updateMessage,
      setStatus,
      setIsStreaming,
      setStreamingMessageId,
      setSessionId,
    ],
  );

  const clearMessages = useCallback(async () => {
    if (animationIntervalRef.current) clearInterval(animationIntervalRef.current);
    clearLocalMessages();
    if (companyId && sessionId) {
      try {
        await aiChatApi.clearHistory(companyId, sessionId);
      } catch {
        // best-effort
      }
    }
  }, [companyId, sessionId, clearLocalMessages]);

  const restoreLastConversation = useCallback(async () => {
    if (!companyId) return;
    try {
      const result = await aiChatApi.getHistory(companyId);
      if (!result.sessionId || result.totalMessages === 0) return;
      const restored: IChatMessage[] = result.history
        .filter((m) => m.role !== 'tool')
        .map((m) => ({
          id: crypto.randomUUID(),
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: m.timestamp,
        }));
      setMessages(restored);
      setSessionId(result.sessionId);
    } catch {
      // best-effort
    }
  }, [companyId, setMessages, setSessionId]);

  return { messages, status, isStreaming, streamingMessageId, sendMessage, clearMessages, restoreLastConversation };
}
